import { useMemo } from 'react';

/**
 * Platform detection + capability flags.
 *
 * PeopleiKnow is currently a web app, but we're laying the groundwork for a
 * React Native shell (iOS + Android) that will lift several browser-only
 * limitations. This hook is the single source of truth for "what can we
 * actually do on the current runtime?" — every feature that has a web
 * limitation should branch on capability flags here instead of sniffing
 * `navigator`/`window.ReactNativeWebView` in the component.
 *
 * ## How native detection works
 *
 * Two signals are checked, in priority order:
 *
 *   1. `navigator.product === 'ReactNative'` — true in a *pure* React Native
 *      build (no embedded WebView).
 *   2. `window.ReactNativeWebView` — injected by `react-native-webview` when
 *      our existing web SPA is wrapped in a thin native shell (the most
 *      likely first-step migration).
 *
 * If either is present we treat the runtime as native. Otherwise it's a
 * regular browser.
 *
 * ## Adding new capability flags
 *
 * When a future feature has a web limitation that disappears on native, add
 * a derived flag to `caps` below with a comment explaining the rationale.
 * Components should consume capability flags, NOT call `platform === 'web'`
 * directly — that keeps the wiring intent-revealing and easy to audit.
 */
export function usePlatformCapabilities() {
  return useMemo(() => {
    const isReactNative =
      (typeof navigator !== 'undefined' && navigator.product === 'ReactNative') ||
      (typeof window !== 'undefined' && Boolean(window.ReactNativeWebView));

    const platform = isReactNative ? 'native' : 'web';
    // OS hint — best-effort, only used for cosmetic copy ("iOS Safari"
    // wording in the SMS warning). Don't gate logic on this; rely on
    // capability flags instead.
    const os = detectOs();

    /**
     * Reliable multi-recipient SMS. Web `sms:A,B?body=…` is unreliable —
     * iOS Safari drops the second recipient, some Android browsers handle
     * it inconsistently. Native shells use `MFMessageComposeViewController`
     * (iOS) or `Intent.ACTION_SENDTO` with multiple recipients (Android),
     * both of which pre-fill the full recipient list.
     */
    const canSendGroupSms = isReactNative;

    /**
     * Native push notifications. Web push works on Chrome/Firefox/Edge but
     * is famously broken on iOS Safari outside of Add-to-Home-Screen PWAs.
     * Native gives us reliable cross-platform push, including silent /
     * background delivery — essential for the daily/weekly comms digest
     * and reminder features.
     */
    const canReceivePush = isReactNative;

    /**
     * Native Contacts API access. Web has no spec-stable way to read the
     * user's address book (Contact Picker API exists in Chromium only,
     * mobile-only, no batch read). Native gets full read/write access,
     * making "import from phone" a one-tap operation instead of CSV/VCF.
     */
    const canImportNativeContacts = isReactNative;

    /**
     * Background mic access. Web requires an active user gesture for every
     * `getUserMedia()` call and the recording stops on tab background.
     * Native can keep the mic open through screen-locks and background
     * states — opens the door to longer-form voice notes and live
     * transcription if we ever want them.
     */
    const canRecordInBackground = isReactNative;

    /**
     * Share extensions / native share sheet. Web `navigator.share` exists
     * but is gated to https + user gesture + supported MIME types, and is
     * absent on desktop Firefox/Edge. Native gives us a uniform OS share
     * sheet plus *Share Extensions* (e.g., saving a LinkedIn contact
     * without leaving LinkedIn).
     */
    const canUseShareExtension = isReactNative;

    return {
      platform,                 // 'web' | 'native'
      isWeb: !isReactNative,
      isNative: isReactNative,
      os,                       // 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'unknown'
      caps: {
        canSendGroupSms,
        canReceivePush,
        canImportNativeContacts,
        canRecordInBackground,
        canUseShareExtension,
      },
    };
  }, []);
}

function detectOs() {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = (navigator.userAgent || '').toLowerCase();
  // iOS 13+ on iPad reports as "Macintosh" with touch, so we check for it explicitly.
  if (/iphone|ipod/.test(ua)) return 'ios';
  if (/ipad/.test(ua) || (ua.includes('macintosh') && (navigator.maxTouchPoints || 0) > 1)) return 'ios';
  if (/android/.test(ua)) return 'android';
  if (/mac os x/.test(ua)) return 'macos';
  if (/windows/.test(ua)) return 'windows';
  if (/linux/.test(ua)) return 'linux';
  return 'unknown';
}

export default usePlatformCapabilities;
